import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Modal,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { COLOR_RED, COLOR_PRIMARY } from 'src/const';
import { getSeasonalityBarColor } from 'src/utils/functions';
import ImageInput from '../Inputs/ImageInput';

interface DownloadReportModalProps {
  open: boolean;
  onClose: () => void;
  onDownload: (settings: {
    logo?: string;
    createdBy: string;
    seasonality: string | null;
    trend: number | null;
    percentile: string;
  }) => void;
  userName: string;
  isDownloading?: boolean;
  seasonality: number | null | 'loading';
  trend: number | null | 'loading';
  percentile: string;
  compsStatus: 'fetching' | 'success' | 'failed';
  downloadablePdfUrl: string | null;
  organizationLogo: string | null;
}

const DownloadReportModal: React.FC<DownloadReportModalProps> = ({
  open,
  onClose,
  onDownload,
  userName,
  isDownloading = false,
  seasonality,
  trend,
  percentile,
  compsStatus,
  downloadablePdfUrl,
  organizationLogo,
}) => {
  const [createdBy, setCreatedBy] = useState(userName);
  const [logo, setLogo] = useState<string[]>([]);

  useEffect(() => {
    console.log(logo);
  }, [logo]);

  const handleDownload = () => {
    onDownload({
      logo: logo[0] || undefined,
      createdBy,
      seasonality:
        seasonality == 'loading' || seasonality == null
          ? null
          : getSeasonalityBarColor(seasonality, true),
      trend: trend == 'loading' || trend == null ? null : trend,
      percentile,
    });
  };

  const isDisabled = compsStatus == 'fetching' || seasonality == 'loading' || trend == 'loading';

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          border: 'none',
          outline: 'none',
          width: 'clamp(300px, 100%, 500px)',
          padding: '30px',
          borderRadius: '12px',
          background: '#fff',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: '4px',
            right: '4px',
          }}
        >
          <CloseRoundedIcon sx={{ color: COLOR_RED }} />
        </IconButton>

        {downloadablePdfUrl ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6">Report Generated Successfully</Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary">
                Your report is now ready for viewing. Click the button below to open it.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                component="a"
                href={downloadablePdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewRoundedIcon />}
                sx={{ textTransform: 'none', color: 'white' }}
                fullWidth
              >
                Open Report PDF
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                If the report does not open automatically, please click the button above.
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Brand Settings
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Created By"
                value={createdBy}
                onChange={e => setCreatedBy(e.target.value)}
                placeholder="Enter name"
              />
            </Grid>

            {organizationLogo && (
              <Grid item xs={12}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  Using Organization Logo
                </Typography>
                <Box
                  component="img"
                  src={organizationLogo}
                  alt="Organization Logo"
                  sx={{
                    width: '150px',
                    height: 'auto',
                    objectFit: 'contain',
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    p: 1,
                    mt: 0,
                    pt: 0,
                  }}
                />
              </Grid>
            )}

            <Grid container item xs={12} direction="column" spacing={2}>
              <ImageInput cta="Upload Logo" images={logo} setImages={setLogo} />
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleDownload}
                disabled={isDisabled}
                sx={{
                  background: COLOR_PRIMARY,
                  '&:hover': {
                    background: COLOR_PRIMARY,
                  },
                }}
              >
                {isDownloading ? <CircularProgress size={24} color="inherit" /> : 'Download Report'}
              </Button>
              {isDisabled && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1,
                    color: 'text.secondary',
                    textAlign: 'center',
                  }}
                >
                  Please wait while we load the complete report...
                </Typography>
              )}
            </Grid>
          </Grid>
        )}
      </Box>
    </Modal>
  );
};

export default DownloadReportModal;
